/**
 * Administrar etapas — 2 capas:
 *  1. Grupos visuales PERSONALIZABLES (Futuros y otros) organizan estados.
 *  2. El ciclo KPI de cada estado sigue siendo fijo de tres valores.
 *  3. Mapeo: cada etapa real Kommo → un estado interno.
 * @module-isolated analytics-config-stages
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStageConfig, persistStageConfig } from '../services/stagesService.js';
import { cacheInvalidatePrefix } from '../../../shared/utils/session-cache.js';
import {
  DEFAULT_STATE_GROUPS,
  REQUIRED_GROUP_IDS,
  groupIdForState,
  metricCycleForState,
  nextNewGroup,
  normalizeStateGroups,
  validateStateGroups
} from '../utils/state-groups.js';

const RESULTADO_OPTS = [
  { id: 'ganado', label: 'Ganado' },
  { id: 'perdido', label: 'Perdido' },
  { id: 'no_aplica', label: 'No aplica' }
];

// Ciclo analítico (fijo) usado por métricas; NO equivale al grupo visual.
const CICLO_OPTS = [
  { id: 'sin_procesar', label: 'Sin procesar' },
  { id: 'en_proceso', label: 'En proceso' },
  { id: 'terminado', label: 'Terminado' }
];

function cicloOf(state) {
  return metricCycleForState(state);
}

function cicloLabel(ciclo) {
  const opt = CICLO_OPTS.find((c) => c.id === ciclo);
  return opt ? opt.label : 'Sin procesar';
}

/** Campos que definen cada ciclo, para crear o mover un estado. */
function cicloPatch(ciclo, prev) {
  if (ciclo === 'terminado') {
    return {
      condition: 'cerrado',
      fase: '',
      countsAsContacted: true,
      resultado: (prev && prev.resultado) || 'no_aplica'
    };
  }
  if (ciclo === 'en_proceso') {
    return { condition: 'abierto', fase: 'en_proceso', countsAsContacted: true, resultado: '' };
  }
  return { condition: 'abierto', fase: 'contacto', countsAsContacted: false, resultado: '' };
}

function slugify(s) {
  const base = String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || 'estado_' + Math.random().toString(36).slice(2, 7);
}

function normalizeState(s, idx) {
  const condition = s.condition === 'cerrado' ? 'cerrado' : 'abierto';
  let resultado = String(s.resultado || '').toLowerCase();
  if (condition === 'abierto') resultado = '';
  if (condition === 'cerrado' && !['ganado', 'perdido', 'no_aplica'].includes(resultado)) {
    resultado = 'no_aplica';
  }
  let fase = String(s.fase || '').toLowerCase();
  if (condition === 'cerrado') fase = '';
  else if (!['contacto', 'en_proceso'].includes(fase)) fase = 'contacto';
  let critDays = null;
  if (condition !== 'cerrado' && s.critDays !== '' && s.critDays != null) {
    const n = Number(s.critDays);
    if (!Number.isNaN(n) && n >= 0) critDays = Math.floor(n);
  }
  return {
    stateId: String(s.stateId || slugify(s.name)).trim(),
    name: String(s.name || s.stateId || 'Estado').trim(),
    condition,
    resultado,
    fase,
    countsAsContacted:
      s.countsAsContacted === true || condition === 'cerrado' ? true : s.countsAsContacted === true,
    critDays,
    order: s.order != null && s.order !== '' ? Number(s.order) : idx + 1,
    groupId: groupIdForState(s)
  };
}

function sortStages(list) {
  return (list || []).slice().sort((a, b) => {
    const pa = String(a.pipelineName || a.pipelineId || '');
    const pb = String(b.pipelineName || b.pipelineId || '');
    if (pa !== pb) return pa.localeCompare(pb, 'es');
    const oa = Number(a.order);
    const ob = Number(b.order);
    if (!isNaN(oa) && !isNaN(ob) && oa !== ob) return oa - ob;
    return String(a.name || a.stageId || '').localeCompare(String(b.name || b.stageId || ''), 'es');
  });
}

export default function StagesConfigPanel() {
  const [states, setStates] = useState([]);
  const [stages, setStages] = useState([]);
  const [groups, setGroups] = useState(() => normalizeStateGroups(DEFAULT_STATE_GROUPS));
  const [groupsApiAvailable, setGroupsApiAvailable] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState({ msg: '', level: '' });
  const [pipelineFilter, setPipelineFilter] = useState('');

  const setStatusMsg = useCallback((msg, level) => {
    setStatus({ msg: msg || '', level: level || '' });
  }, []);

  const load = useCallback(
    async (opts) => {
      const sync = !!(opts && opts.syncEmbudos);
      setBusy(sync ? 'sync' : 'load');
      setStatusMsg(sync ? 'Sincronizando Embudos…' : 'Cargando…');
      try {
        const res = await loadStageConfig({ syncEmbudos: sync });
        if (!res || res.success === false) {
          throw new Error((res && res.error) || 'No se pudo cargar la configuración.');
        }
        setStates((res.internalStates || []).map(normalizeState));
        setStages(sortStages(res.items || []));
        setGroups(normalizeStateGroups(res.stateGroups));
        setGroupsApiAvailable(Array.isArray(res.stateGroups));
        setDirty(false);
        const parts = [(res.total != null ? res.total : (res.items || []).length) + ' etapas'];
        if (res.seededAdded > 0) parts.push('+' + res.seededAdded + ' nuevas');
        if (!Array.isArray(res.stateGroups)) {
          parts.push('Grupos personalizados requieren desplegar la API Analytics actualizada');
        }
        setStatusMsg(parts.join(' · '), Array.isArray(res.stateGroups) ? 'ok' : 'warn');
      } catch (err) {
        console.error('[analytics-config] stages load', err);
        setStatusMsg((err && err.message) || 'Error al cargar', 'error');
      } finally {
        setBusy('');
        setLoading(false);
      }
    },
    [setStatusMsg]
  );

  useEffect(() => {
    load();
  }, [load]);

  const statesById = useMemo(() => {
    const m = {};
    states.forEach((s) => {
      m[s.stateId] = s;
    });
    return m;
  }, [states]);

  const byGroup = useMemo(() => {
    const acc = Object.fromEntries(groups.map((g) => [g.groupId, []]));
    states.forEach((state) => {
      const groupId = groupIdForState(state);
      if (!acc[groupId]) acc[groupId] = [];
      acc[groupId].push(state);
    });
    return acc;
  }, [groups, states]);

  const pipelines = useMemo(() => {
    const map = new Map();
    stages.forEach((it) => {
      const id = String(it.pipelineId || '');
      if (!id) return;
      if (!map.has(id)) map.set(id, it.pipelineName || id);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }, [stages]);

  const visibleStages = useMemo(() => {
    if (!pipelineFilter) return stages;
    return stages.filter((it) => String(it.pipelineId) === pipelineFilter);
  }, [stages, pipelineFilter]);

  const grouped = useMemo(() => {
    const byState = {};
    stages.forEach((it) => {
      const id = it.internalStateId || '(sin asignar)';
      byState[id] = (byState[id] || 0) + 1;
    });
    return byState;
  }, [stages]);

  function markDirty() {
    setDirty(true);
    setStatusMsg('Cambios sin guardar', 'warn');
  }

  function updateState(stateId, patch) {
    setStates((prev) =>
      prev.map((s) => {
        if (s.stateId !== stateId) return s;
        const next = { ...s, ...patch };
        if (next.condition === 'abierto') {
          next.resultado = '';
          if (!['contacto', 'en_proceso'].includes(next.fase)) next.fase = 'contacto';
        }
        if (next.condition === 'cerrado') {
          next.fase = '';
          if (!next.resultado) next.resultado = 'no_aplica';
        }
        return next;
      })
    );
    markDirty();
  }

  function setCiclo(stateId, ciclo) {
    setStates((prev) =>
      prev.map((s) => {
        if (s.stateId !== stateId) return s;
        // Si aún está en el grupo base del ciclo anterior, acompañar el cambio.
        // Si está en Futuros u otro custom, no moverlo involuntariamente.
        const groupId = groupIdForState(s) === cicloOf(s) ? ciclo : groupIdForState(s);
        return { ...s, ...cicloPatch(ciclo, s), groupId };
      })
    );
    markDirty();
  }

  function moveToGroup(stateId, groupId) {
    if (!groups.some((group) => group.groupId === groupId)) return;
    updateState(stateId, { groupId });
  }

  function renameGroup(groupId, name) {
    if (!groupsApiAvailable) return;
    setGroups((prev) => prev.map((g) => g.groupId === groupId ? { ...g, name } : g));
    markDirty();
  }

  function addGroup() {
    if (!groupsApiAvailable) return;
    setGroups((prev) => prev.concat(nextNewGroup(prev)));
    markDirty();
  }

  function removeGroup(groupId) {
    if (!groupsApiAvailable || REQUIRED_GROUP_IDS.includes(groupId)) return;
    const inUse = states.some((state) => groupIdForState(state) === groupId);
    if (inUse) {
      setStatusMsg('Mové primero los estados de este grupo a otro antes de eliminarlo.', 'warn');
      return;
    }
    setGroups((prev) => prev.filter((group) => group.groupId !== groupId));
    markDirty();
  }

  function shiftGroup(groupId, direction) {
    if (!groupsApiAvailable) return;
    setGroups((prev) => {
      const ordered = normalizeStateGroups(prev);
      const index = ordered.findIndex((g) => g.groupId === groupId);
      const next = index + direction;
      if (index < 0 || next < 0 || next >= ordered.length) return prev;
      [ordered[index], ordered[next]] = [ordered[next], ordered[index]];
      return ordered.map((group, i) => ({ ...group, order: i + 1 }));
    });
    markDirty();
  }

  function addState(groupId) {
    // Los estados nuevos de Futuros permanecen comercialmente abiertos.
    const ciclo = groupId === 'terminado' ? 'terminado'
      : groupId === 'sin_procesar' ? 'sin_procesar' : 'en_proceso';
    const base = ciclo === 'terminado' ? 'Nuevo cierre' : 'Nuevo estado';
    let name = base;
    let n = 1;
    const names = new Set(states.map((s) => s.name.toLowerCase()));
    while (names.has(name.toLowerCase())) {
      n += 1;
      name = base + ' ' + n;
    }
    let id = slugify(name);
    const ids = new Set(states.map((s) => s.stateId));
    while (ids.has(id)) id = slugify(name) + '_' + Math.random().toString(36).slice(2, 5);
    const order = (states.reduce((mx, s) => Math.max(mx, s.order || 0), 0) || 0) + 1;
    setStates((prev) =>
      prev.concat(
        normalizeState({ stateId: id, name, order, groupId, ...cicloPatch(ciclo, null) }, prev.length)
      )
    );
    markDirty();
  }

  function removeState(stateId) {
    const used = stages.filter((it) => it.internalStateId === stateId).length;
    if (used > 0) {
      const ok = window.confirm(
        used + ' etapa(s) usan este estado. Se reasignarán al primer estado disponible. ¿Continuar?'
      );
      if (!ok) return;
    }
    setStates((prev) => {
      const next = prev.filter((s) => s.stateId !== stateId);
      const fallbackOpen = next.find((s) => s.condition === 'abierto');
      const fallbackClosed = next.find((s) => s.condition === 'cerrado');
      const removed = prev.find((s) => s.stateId === stateId);
      if (removed) {
        const fb =
          removed.condition === 'cerrado'
            ? fallbackClosed || fallbackOpen
            : fallbackOpen || fallbackClosed;
        if (fb) {
          setStages((sPrev) =>
            sPrev.map((it) =>
              it.internalStateId === stateId
                ? { ...it, internalStateId: fb.stateId, internalStateName: fb.name }
                : it
            )
          );
        }
      }
      return next;
    });
    markDirty();
  }

  function assignStage(pipelineId, stageId, internalStateId) {
    const st = statesById[internalStateId];
    setStages((prev) =>
      prev.map((it) =>
        String(it.pipelineId) === String(pipelineId) && String(it.stageId) === String(stageId)
          ? {
              ...it,
              internalStateId,
              internalStateName: st ? st.name : internalStateId,
              estado: st ? st.condition : it.estado
            }
          : it
      )
    );
    markDirty();
  }

  function bulkAssignVisible(internalStateId) {
    if (!internalStateId) return;
    const st = statesById[internalStateId];
    const ids = new Set(visibleStages.map((it) => String(it.pipelineId) + ':' + String(it.stageId)));
    setStages((prev) =>
      prev.map((it) =>
        ids.has(String(it.pipelineId) + ':' + String(it.stageId))
          ? { ...it, internalStateId, internalStateName: st ? st.name : internalStateId }
          : it
      )
    );
    markDirty();
  }

  async function runSave() {
    if (!stages.length) {
      setStatusMsg('No hay etapas para guardar.', 'warn');
      return;
    }
    if (!states.length) {
      setStatusMsg('Definí al menos un estado interno.', 'warn');
      return;
    }
    if (groupsApiAvailable) {
      const invalid = validateStateGroups(groups, states);
      if (invalid) {
        setStatusMsg(invalid, 'error');
        return;
      }
    }
    setBusy('save');
    setStatusMsg('Guardando…');
    try {
      const stagePayload = stages.map((it) => ({
        pipelineId: it.pipelineId,
        stageId: it.stageId,
        pipelineName: it.pipelineName || '',
        name: it.name || '',
        order: it.order,
        internalStateId: it.internalStateId,
        slaHours: it.slaHours
      }));
      const statePayload = states.map((s, i) => normalizeState(s, i));
      const res = await persistStageConfig(stagePayload, {
        internalStates: statePayload,
        stateGroups: groupsApiAvailable ? normalizeStateGroups(groups) : null,
        onProgress: (msg) => setStatusMsg(msg)
      });
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudo guardar.');
      }
      if (Array.isArray(res.internalStates)) {
        setStates(res.internalStates.map(normalizeState));
      }
      if (Array.isArray(res.stateGroups)) {
        setGroups(normalizeStateGroups(res.stateGroups));
        setGroupsApiAvailable(true);
      }
      setStages(sortStages(res.items || stages));
      // La página de cohortes puede seguir usando datos de la sesión anterior:
      // invalidar después del guardado para que nombre/mapeo GI se reflejen
      // al navegar de vuelta, sin resincronizar Kommo.
      cacheInvalidatePrefix('cohort:');
      setDirty(false);
      setStatusMsg(res.message || 'Guardado.', 'ok');
    } catch (err) {
      console.error('[analytics-config] stages save', err);
      setStatusMsg((err && err.message) || 'Error al guardar', 'error');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="analytics-stages-panel">
      <p className="analytics-section-note" style={{ marginBottom: '0.75rem' }}>
        <strong>Grupos personalizables:</strong> Sin procesar, En proceso, Futuros y Terminado.
        {' '}Podés renombrar los grupos, agregar otros y mover tus estados entre ellos.
        {' '}<strong>Ciclo comercial:</strong> conserva internamente tres valores (Sin procesar, En proceso y Terminado)
        para métricas; mover un estado al grupo Futuros no modifica automáticamente ese ciclo.
        {' '}Las asociaciones con Kommo se mantienen en <code>Sales_Stage_Map</code>.
      </p>

      <div className="analytics-stage-toolbar">
        <button
          type="button"
          className="analytics-btn analytics-btn-ghost"
          disabled={!!busy}
          onClick={() => load({ syncEmbudos: true })}
        >
          {busy === 'sync' ? 'Sincronizando…' : 'Sincronizar etapas de Kommo'}
        </button>
        <button
          type="button"
          className="analytics-btn"
          disabled={!!busy || !dirty}
          onClick={runSave}
        >
          {busy === 'save' ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <span
          className={'analytics-stages-status' + (status.level ? ' is-' + status.level : '')}
          aria-live="polite"
        >
          {status.msg}
        </span>
      </div>

      {loading ? <p className="analytics-empty">Cargando…</p> : null}

      {!loading ? (
        <>
          <section className="analytics-panel" aria-label="Administrar grupos de estados GI Capital"
            style={{ marginBottom: '1.25rem' }}>
            <div className="analytics-section-head">
              <h3 className="analytics-section-title">Grupos para organizar tus estados</h3>
              <button type="button" className="analytics-btn analytics-btn-ghost"
                disabled={!!busy || !groupsApiAvailable} onClick={addGroup}>+ Grupo</button>
            </div>
            {!groupsApiAvailable ? (
              <p className="analytics-section-note" role="status">
                Para modificar los grupos, primero se debe publicar la nueva versión de la API de Analytics.
                Las demás configuraciones siguen funcionando.
              </p>
            ) : null}
            <div className="reu-split" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
              {groups.map((group, index) => (
                <div key={group.groupId} style={{ flex: '1 1 210px', minWidth: 0 }}>
                  <label className="analytics-field">
                    <span>Nombre del grupo</span>
                    <input type="text" value={group.name} maxLength={90}
                      aria-label={'Nombre de grupo ' + group.groupId}
                      disabled={!!busy || !groupsApiAvailable}
                      onChange={(e) => renameGroup(group.groupId, e.target.value)} />
                  </label>
                  <div className="analytics-stage-toolbar" style={{ marginTop: '0.35rem', gap: '0.25rem' }}>
                    <button type="button" className="analytics-btn analytics-btn-ghost"
                      aria-label={'Mover grupo ' + group.name + ' a la izquierda'}
                      disabled={!!busy || !groupsApiAvailable || index === 0}
                      onClick={() => shiftGroup(group.groupId, -1)}>←</button>
                    <button type="button" className="analytics-btn analytics-btn-ghost"
                      aria-label={'Mover grupo ' + group.name + ' a la derecha'}
                      disabled={!!busy || !groupsApiAvailable || index === groups.length - 1}
                      onClick={() => shiftGroup(group.groupId, 1)}>→</button>
                    {!REQUIRED_GROUP_IDS.includes(group.groupId) ? (
                      <button type="button" className="analytics-btn analytics-btn-ghost"
                        disabled={!!busy || !groupsApiAvailable}
                        title="Solo se puede eliminar un grupo sin estados asignados"
                        onClick={() => removeGroup(group.groupId)}>Eliminar</button>
                    ) : (
                      <span className="analytics-sub">Grupo base</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="analytics-sub">
              Los nombres son libres. Los grupos base se pueden renombrar, pero no eliminar.
              {' '}Los grupos adicionales pueden eliminarse cuando no contienen estados.
            </p>
          </section>

          {/* Estados internos organizados por grupo visual, no por ciclo KPI. */}
          <div
            className="reu-split"
            style={{ marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            {groups.map((col) => {
              const list = byGroup[col.groupId] || [];
              return (
                <div
                  key={col.groupId}
                  className="analytics-panel"
                  style={{ flex: '1 1 280px', minWidth: 0 }}
                >
                  <div className="analytics-section-head" style={{ marginBottom: '0.25rem' }}>
                    <h3 className="analytics-section-title" style={{ margin: 0 }}>
                      <span className="analytics-ciclo-badge">{col.name}</span>{' '}
                      · {list.length}
                    </h3>
                    <button
                      type="button"
                      className="analytics-btn analytics-btn-ghost"
                      disabled={!!busy || (!groupsApiAvailable && !REQUIRED_GROUP_IDS.includes(col.groupId))}
                      onClick={() => addState(col.groupId)}
                    >
                      + Estado
                    </button>
                  </div>
                  <p className="analytics-sub" style={{ marginBottom: '0.5rem' }}>
                    Grupos de organización · {list.length} estado(s) de GI Capital
                  </p>
                  {list.length ? (
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>Nombre (tuyo)</th>
                          <th>Organización / ciclo KPI</th>
                          <th title="Días desde creación para marcar el lead como crítico. Vacío usa el default del ciclo.">
                            Crítico &gt; días
                          </th>
                          <th aria-label="acciones" />
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((s) => (
                          <tr key={s.stateId}>
                            <td>
                              <input
                                type="text"
                                className="reu-inline-input"
                                value={s.name}
                                disabled={!!busy}
                                onChange={(e) => updateState(s.stateId, { name: e.target.value })}
                              />
                              <span className="analytics-sub">
                                {grouped[s.stateId] || 0} etapa(s)
                              </span>
                            </td>
                            <td>
                              <select
                                value={groupIdForState(s)}
                                disabled={!!busy || !groupsApiAvailable}
                                aria-label={'Grupo de organización de ' + s.name}
                                onChange={(e) => moveToGroup(s.stateId, e.target.value)}
                              >
                                {groups.map((g) => (
                                  <option key={g.groupId} value={g.groupId}>{g.name}</option>
                                ))}
                              </select>
                              <select
                                style={{ marginTop: '0.25rem' }}
                                value={cicloOf(s)}
                                disabled={!!busy}
                                aria-label={'Ciclo comercial de ' + s.name}
                                onChange={(e) => setCiclo(s.stateId, e.target.value)}
                              >
                                {CICLO_OPTS.map((c) => (
                                  <option key={c.id} value={c.id}>{c.label}</option>
                                ))}
                              </select>
                              {cicloOf(s) === 'terminado' ? (
                                <select
                                  style={{ marginTop: '0.25rem' }}
                                  value={s.resultado || 'no_aplica'}
                                  disabled={!!busy}
                                  aria-label={'Resultado de ' + s.name}
                                  onChange={(e) =>
                                    updateState(s.stateId, { resultado: e.target.value })
                                  }
                                >
                                  {RESULTADO_OPTS.map((r) => (
                                    <option key={r.id} value={r.id}>
                                      {r.label}
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                            </td>
                            <td>
                              {cicloOf(s) === 'terminado' ? (
                                <span className="analytics-sub">No aplica</span>
                              ) : (
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  className="reu-inline-input"
                                  style={{ width: '5.5rem' }}
                                  value={s.critDays == null ? '' : s.critDays}
                                  placeholder={cicloOf(s) === 'en_proceso' ? '15' : '1'}
                                  disabled={!!busy}
                                  aria-label={'Días para crítico de ' + s.name}
                                  onChange={(e) =>
                                    updateState(s.stateId, {
                                      critDays: e.target.value === '' ? null : e.target.value
                                    })
                                  }
                                />
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="analytics-btn analytics-btn-ghost"
                                disabled={!!busy}
                                title="Eliminar estado"
                                onClick={() => removeState(s.stateId)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="analytics-empty">Sin estados en «{col.name}».</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Capa 2: mapeo Kommo → estado interno */}
          <SectionMappingToolbar
            pipelines={pipelines}
            pipelineFilter={pipelineFilter}
            setPipelineFilter={setPipelineFilter}
            states={states}
            busy={busy}
            onBulk={bulkAssignVisible}
          />

          {visibleStages.length ? (
            <div className="analytics-panel analytics-panel-scroll" style={{ padding: 0 }}>
              <table className="analytics-table" id="analytics-stages-table">
                <thead>
                  <tr>
                    <th title="Nombre exacto en Kommo.">Etapa (Kommo)</th>
                    <th title="Embudo exacto en Kommo.">Embudo (Kommo)</th>
                    <th title="Estado interno GI Capital asignado.">Estado interno</th>
                    <th title="El grupo visual y el ciclo KPI se administran por separado.">Grupo / ciclo KPI</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStages.map((it) => {
                    const st = statesById[it.internalStateId];
                    return (
                      <tr key={String(it.pipelineId) + ':' + String(it.stageId)}>
                        <td>
                          <strong>{it.name || it.stageId || '—'}</strong>
                          <span className="analytics-sub">ID {it.stageId || '—'}</span>
                        </td>
                        <td>
                          {it.pipelineName || it.pipelineId || '—'}
                          <span className="analytics-sub">ID {it.pipelineId || '—'}</span>
                        </td>
                        <td>
                          <select
                            value={it.internalStateId || ''}
                            disabled={!!busy}
                            aria-label={'Estado interno ' + (it.name || it.stageId)}
                            onChange={(e) =>
                              assignStage(it.pipelineId, it.stageId, e.target.value)
                            }
                          >
                            {!st ? <option value="">— Sin asignar —</option> : null}
                            {groups.map((col) => (
                              <optgroup key={col.groupId} label={col.name}>
                                {(byGroup[col.groupId] || []).map((s) => (
                                  <option key={s.stateId} value={s.stateId}>
                                    {s.name}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </td>
                        <td>
                          {st ? (
                            <>
                              <strong>{groups.find((g) => g.groupId === groupIdForState(st))?.name || 'Sin grupo'}</strong>
                              <span className="analytics-sub">Ciclo KPI: {cicloLabel(cicloOf(st))}</span>
                            </>
                          ) : (
                            <span className="analytics-sub">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="analytics-empty">
              Sin etapas. Pulsa «Sincronizar etapas de Kommo».
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}

function SectionMappingToolbar({
  pipelines,
  pipelineFilter,
  setPipelineFilter,
  states,
  busy,
  onBulk
}) {
  const [bulkState, setBulkState] = useState('');
  return (
    <div className="analytics-stage-toolbar" style={{ marginTop: '0.5rem' }}>
      <label className="analytics-field" style={{ margin: 0 }}>
        <span>Embudo Kommo</span>
        <select
          value={pipelineFilter}
          onChange={(e) => setPipelineFilter(e.target.value)}
          disabled={!!busy}
        >
          <option value="">Todos</option>
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <label className="analytics-field" style={{ margin: 0 }}>
        <span>Asignar visibles a</span>
        <select
          value={bulkState}
          onChange={(e) => setBulkState(e.target.value)}
          disabled={!!busy}
        >
          <option value="">—</option>
          {states.map((s) => (
            <option key={s.stateId} value={s.stateId}>
              {s.name} ({cicloLabel(cicloOf(s))})
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="analytics-btn analytics-btn-ghost"
        disabled={!!busy || !bulkState}
        onClick={() => onBulk(bulkState)}
      >
        Aplicar a visibles
      </button>
    </div>
  );
}
